import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { COMPANY, FLEET_SPECS } from "../data/siteData";
import { imageUrl } from "../lib/images";

export default function OmOsPage() {
  return (
    <div className="bg-[#070d18] text-slate-100 min-h-screen">
      <SEOHead
        title="Om KVC Express"
        description="Læs om KVC Express ApS, vores tilgang til professionel køle- og medicinaltransport samt investeringer i moderne materiel og B2B-relationer."
      />

      {/* Hero */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 border-b border-slate-800 bg-[#081224] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest">
              <i className="fa-solid fa-building text-[10px]" aria-hidden="true" />
              <span>Virksomhedsprofil · B2B Transport</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight font-display">
              Kvalitet, driftssikkerhed og langsigtede B2B-relationer
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              KVC Express ApS er en specialiseret transportvirksomhed med dedikeret fokus på køle-, fødevare- og medicinaltransport i hele Danmark – inklusive Bornholm. Vores forretning er bygget på godkendte chauffører, moderne kølevogne og faste transportaftaler.
            </p>
          </div>
        </div>
      </section>

      {/* Company Philosophy & Approach */}
      <section className="py-20 bg-[#070d18] border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="text-xs font-mono uppercase tracking-widest text-[#2DD4BF]">
                Vores tilgang
              </div>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight font-display">
                Fokus på faste transportaftaler
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                I en branche præget af store krav til punktlighed og temperaturfølsomhed har vi valgt en klar kurs: Vi henvender os til virksomheder, der efterspørger en stabil, engageret transportpartner til løbende kørselsbehov.
              </p>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Vi indgår transportaftaler med virksomheder og leverer til både erhvervs- og privatadresser med fokus på faste køreplaner og stabil temperaturkontrol.
              </p>

              <div className="p-6 rounded-2xl bg-[#0b1426] border border-slate-800 space-y-4">
                <h3 className="font-bold text-white text-base">Virksomhedens nøgleprincipper:</h3>
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#2DD4BF] mt-0.5" aria-hidden="true" />
                    <span><strong>Moderne kølevogne:</strong> Vores flåde består af nye diesel- og elbiler med aktiv køl.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#2DD4BF] mt-0.5" aria-hidden="true" />
                    <span><strong>Uddannede chauffører:</strong> Lovpligtigt chaufføruddannelsesbevis samt kurser i køle- og medicintransport.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#2DD4BF] mt-0.5" aria-hidden="true" />
                    <span><strong>Landsdækkende:</strong> Faste ruter i hele Danmark – inklusive Bornholm.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#2DD4BF] mt-0.5" aria-hidden="true" />
                    <span><strong>Høj hygiejne:</strong> Pletfri rengøring og vedligeholdelse af varerum til følsomme varer.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden border border-slate-700 bg-slate-900 shadow-2xl">
                <img
                  src={imageUrl("/_assets/bornholmslinjen.jpeg", "width=900,quality=85,format=auto")}
                  alt="KVC Express varebiler ved færgen – landsdækkende dækning inkl. Bornholm"
                  className="w-full h-80 sm:h-[450px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Værdier & Kundeløfte */}
      <section className="py-16 bg-[#081224] border-b border-slate-800 text-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 rounded-2xl overflow-hidden border border-slate-800 shadow-2xl">
              <img
                src={imageUrl("/_assets/shutterstock-2190533271.jpg", "width=800,quality=80,format=auto")}
                alt="KVC Express professionelt chaufførkorps"
                className="w-full h-72 sm:h-80 object-cover"
              />
            </div>
            <div className="lg:col-span-6 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 text-[#2DD4BF] text-xs font-mono uppercase">
                <i className="fa-solid fa-handshake-angle text-[11px]" aria-hidden="true" />
                <span>Professionelt Chaufførkorps</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-display">
                Repræsentation med omhu og høj faglighed
              </h2>
              <p className="text-sm text-slate-300 leading-relaxed">
                Vores chauffører er virksomhedens ansigt udadtil – og jeres forlængede arm over for jeres modtagere. Derfor stiller vi strenge krav til kundeservice, præcision og overholdelse af alle retningslinjer for hygiejne, temperaturlogning og sikker håndtering af varerne.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Official Company Data Box */}
      <section className="py-20 bg-slate-100 text-slate-900 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F766E]/10 border border-[#2DD4BF]/30 text-[#0F766E] text-xs font-mono uppercase tracking-widest mb-3">
              <i className="fa-solid fa-id-card text-[11px]" aria-hidden="true" />
              <span>Virksomhedsdata</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 tracking-tight font-display">
              Officielle virksomhedsoplysninger
            </h2>
            <p className="text-slate-600 text-sm mt-2">
              KVC Express ApS er registreret og hjemmehørende i Storkøbenhavn – med landsdækkende ruter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200/90 shadow-md space-y-4">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0F766E] font-bold">
                Selskabsdetaljer
              </div>
              <div className="space-y-3 text-sm text-slate-700">
                <div className="flex justify-between border-b border-slate-100 pb-2">
                  <span className="text-slate-500">Firmanavn</span>
                  <span className="font-bold text-slate-900">{COMPANY.name}</span>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-2">
                  <span className="text-slate-500">CVR-nummer</span>
                  <span className="font-mono text-slate-900 font-bold">{COMPANY.cvr}</span>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-2">
                  <span className="text-slate-500">Dækningsområde</span>
                  <span className="text-slate-900 font-semibold text-right">Hele Danmark inkl. Bornholm</span>
                </div>
                <div className="flex justify-between pt-1">
                  <span className="text-slate-500">Primær forretning</span>
                  <span className="text-[#0F766E] font-semibold text-right">Køle-, Fødevare- & Medicintransport</span>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200/90 shadow-md space-y-4">
              <div className="text-xs font-mono uppercase tracking-widest text-[#0F766E] font-bold">
                Lokation & B2B Kontakt
              </div>
              <div className="space-y-4 text-sm text-slate-700">
                <div className="flex items-start gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-[#0F766E]/10 border border-[#0F766E]/20 flex items-center justify-center text-[#0F766E] shrink-0 mt-0.5">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{COMPANY.location}</div>
                    <div className="text-slate-500 text-xs mt-0.5">{COMPANY.coverage}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-[#0F766E]/10 border border-[#0F766E]/20 flex items-center justify-center text-[#0F766E] shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <a
                      href={`tel:${COMPANY.phoneRaw}`}
                      className="text-slate-800 hover:text-[#0F766E] font-medium transition whitespace-nowrap block"
                    >
                      <span className="whitespace-nowrap">{COMPANY.phone}</span>
                    </a>
                    <span className="text-[11px] text-slate-500 block">{COMPANY.phoneHours}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-[#0F766E]/10 border border-[#0F766E]/20 flex items-center justify-center text-[#0F766E] shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                  </div>
                  <div>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-slate-800 hover:text-[#0F766E] font-medium transition whitespace-nowrap block"
                    >
                      <span className="whitespace-nowrap">{COMPANY.email}</span>
                    </a>
                    <span className="text-[11px] text-slate-500 block">{COMPANY.emailResponse}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#050a14] text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-white font-display">
            Ønsker I en uforpligtende dialog om et samarbejde?
          </h3>
          <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
            Vi står klar til at gennemgå jeres transportbehov og drøfte mulighederne for en fast samarbejdsaftale.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#0F766E] hover:bg-[#115E59] text-white font-bold text-xs uppercase tracking-wider transition shadow-xl shadow-[#0F766E]/40"
          >
            <span>Kontakt KVC Express</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
